import React from 'react';
import { Button, Checkbox, Flex, SettingsSection, Text, TextField } from '@chatsdet/ui';
import { Plus, X } from '@phosphor-icons/react';
import type { EnvVarDefinition } from '../property-fields';
import { SectionSaveFooter } from '../SectionSaveFooter';
import { usePropertySection } from '../usePropertySection';

/**
 * Declares the variables this application's tests use. Values are per
 * environment; this section only names them.
 */
export function EnvironmentVariablesSection({ propertyId }: { propertyId: string }) {
  const section = usePropertySection({
    propertyId,
    select: (p) => ({
      envVarDefinitions: (p.envVarDefinitions ?? []) as EnvVarDefinition[],
    }),
    // Drop unnamed rows the user added but never filled in
    toPatch: (d) => ({
      envVarDefinitions: d.envVarDefinitions.filter((v) => v.name.trim() !== ''),
    }),
  });

  const definitions = section.draft.envVarDefinitions ?? [];

  const updateDefinition = (index: number, patch: Partial<EnvVarDefinition>) =>
    section.setDraft((d) => ({
      envVarDefinitions: d.envVarDefinitions.map((v, i) => (i === index ? { ...v, ...patch } : v)),
    }));

  const hasDuplicateNames = React.useMemo(() => {
    const names = definitions.map((d) => d.name.trim()).filter((n) => n !== '');
    return new Set(names).size !== names.length;
  }, [definitions]);

  if (section.isLoading) return null;

  return (
    <SettingsSection
      title="Environment Variables"
      description={
        <>
          Declare the variables this application&apos;s tests use (e.g. API_URL). Set each
          environment&apos;s values in the Environments tab; at run time they&apos;re injected as{' '}
          <code className="text-xs">$var.ENV.&lt;NAME&gt;</code>.
        </>
      }
      action={
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<Plus size={14} />}
          onPress={() =>
            section.setDraft((d) => ({
              envVarDefinitions: [...d.envVarDefinitions, { name: '' }],
            }))
          }
        >
          Add Variable
        </Button>
      }
      footer={
        section.isDirty ? (
          <SectionSaveFooter
            isSaving={section.isSaving}
            canSave={!hasDuplicateNames}
            message={hasDuplicateNames ? 'Variable names must be unique' : undefined}
            onSave={section.save}
            onDiscard={section.discard}
          />
        ) : null
      }
      // Rows carry their own padding so they can divide edge to edge.
      className="[&>[data-settings-section-body]]:p-0 [&>[data-settings-section-body]]:space-y-0"
    >
      {definitions.length === 0 ? (
        <Text size="sm" className="text-muted-foreground px-4 py-6 block text-pretty">
          No variables declared yet. Add one to make it available to every environment.
        </Text>
      ) : (
        <div className="divide-y divide-border">
          {definitions.map((def, index) => (
            <div key={index} className="px-4 py-3 space-y-2" data-testid="env-var-definition-row">
              <Flex gap="sm" align="end">
                <div className="flex-1">
                  <TextField
                    label="Name"
                    value={def.name}
                    onChange={(v) => updateDefinition(index, { name: v })}
                    placeholder="API_URL"
                  />
                </div>
                <div className="flex-[2]">
                  <TextField
                    label="Description"
                    value={def.description ?? ''}
                    onChange={(v) => updateDefinition(index, { description: v || undefined })}
                    placeholder="What this variable is for (optional)"
                  />
                </div>
                <Button
                  variant="ghost"
                  aria-label={`Remove variable ${def.name || index + 1}`}
                  onPress={() =>
                    section.setDraft((d) => ({
                      envVarDefinitions: d.envVarDefinitions.filter((_, i) => i !== index),
                    }))
                  }
                  iconLeft={<X size={16} />}
                  className="min-h-10 min-w-10 shrink-0"
                />
              </Flex>
              <Checkbox
                isSelected={def.sensitive ?? false}
                onChange={(checked) => updateDefinition(index, { sensitive: checked || undefined })}
                label="Sensitive (value is masked in the UI and redacted from agent context)"
              />
            </div>
          ))}
        </div>
      )}
      {hasDuplicateNames && (
        <Text size="xs" className="text-destructive px-4 pb-3 block">
          Variable names must be unique.
        </Text>
      )}
    </SettingsSection>
  );
}
